import type { ContentType } from '@sequesto-os/shared';

export interface ExtraFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  defaultValue?: string | number;
}

export const CONTENT_TYPE_FIELDS: Partial<Record<ContentType, ExtraFieldConfig[]>> = {
  comparison_page: [
    { key: 'competitor', label: 'Competitor Name', type: 'text', placeholder: 'e.g., Loopio, Responsive, Qvidian', required: true },
    { key: 'focusAreas', label: 'Focus Areas', type: 'textarea', placeholder: 'e.g., AI capabilities, compliance features, pricing, integrations...' },
    { key: 'buyerProfile', label: 'Buyer Profile', type: 'text', placeholder: 'e.g., VP of Sales Ops at enterprise pharma' },
  ],
  seo_article: [
    { key: 'targetKeyword', label: 'Target Keyword', type: 'text', placeholder: 'e.g., rfp automation software' },
    { key: 'contentLength', label: 'Content Length', type: 'select', defaultValue: 'standard', options: [
      { value: 'standard', label: 'Standard (1,500-2,000 words)' },
      { value: 'long', label: 'Long-form (2,500-3,500 words)' },
      { value: 'pillar', label: 'Pillar (3,500+ words)' },
    ] },
    { key: 'technicalDepth', label: 'Technical Depth', type: 'select', defaultValue: 'practitioner', options: [
      { value: 'executive', label: 'Executive (high-level)' },
      { value: 'practitioner', label: 'Practitioner (mid-depth)' },
      { value: 'technical', label: 'Technical (deep-dive)' },
    ] },
  ],
  faq_page: [
    { key: 'faqCount', label: 'Number of FAQs', type: 'number', placeholder: '10', defaultValue: 10 },
    { key: 'categories', label: 'Categories', type: 'text', placeholder: 'e.g., Pricing, Features, Implementation (comma-separated)' },
  ],
  industry_page: [
    { key: 'industrySegment', label: 'Industry', type: 'text', placeholder: 'e.g., Pharmaceutical, Financial Services', required: true },
    { key: 'regionalFocus', label: 'Regional Focus', type: 'text', placeholder: 'e.g., Europe, North America' },
  ],
  use_case_page: [
    { key: 'useCaseName', label: 'Use Case', type: 'text', placeholder: 'e.g., Multi-department RFP response', required: true },
    { key: 'targetRole', label: 'Target Role', type: 'text', placeholder: 'e.g., Bid Manager, Proposal Writer' },
    { key: 'problemStatement', label: 'Problem Statement', type: 'textarea', placeholder: 'What pain point does this use case solve?' },
  ],
  case_study: [
    { key: 'customerName', label: 'Customer Name', type: 'text', placeholder: 'e.g., Acme Pharma', required: true },
    { key: 'industrySector', label: 'Industry Sector', type: 'text', placeholder: 'e.g., Pharmaceutical, Banking' },
    { key: 'keyResults', label: 'Key Results', type: 'textarea', placeholder: 'e.g., 60% faster RFP response, 3x more bids submitted...' },
  ],
  landing_page: [
    { key: 'primaryCta', label: 'Primary CTA', type: 'text', placeholder: 'e.g., Book a Demo, Start Free Trial' },
    { key: 'targetAudience', label: 'Target Audience', type: 'text', placeholder: 'e.g., Enterprise procurement teams' },
  ],
  integration_page: [
    { key: 'partnerName', label: 'Integration Partner', type: 'text', placeholder: 'e.g., SAP, Salesforce, Microsoft Teams', required: true },
    { key: 'integrationCategory', label: 'Category', type: 'select', defaultValue: 'document_management', options: [
      { value: 'crm', label: 'CRM' },
      { value: 'collaboration', label: 'Collaboration & Communication' },
      { value: 'procurement', label: 'Procurement / e-Sourcing' },
      { value: 'document_management', label: 'Document Management' },
      { value: 'identity', label: 'Identity & SSO' },
      { value: 'erp', label: 'ERP / Business Systems' },
      { value: 'other', label: 'Other' },
    ] },
    { key: 'keyCapabilities', label: 'Key Integration Capabilities', type: 'textarea', placeholder: 'e.g., Bi-directional data sync, auto-import RFP documents, push responses back...' },
  ],
  solution_page: [
    { key: 'solutionName', label: 'Solution Name', type: 'text', placeholder: 'e.g., RFP Automation, Security Questionnaire Automation', required: true },
    { key: 'targetPainPoint', label: 'Problem Statement', type: 'textarea', placeholder: 'What pain point does this solution address?' },
    { key: 'targetIndustry', label: 'Target Industry (optional)', type: 'text', placeholder: 'e.g., Financial Services, Pharma' },
  ],
};

/** Build default extra field values for a content type */
export function getDefaultExtraFields(type: ContentType): Record<string, unknown> {
  const fields = CONTENT_TYPE_FIELDS[type];
  if (!fields) return {};
  const defaults: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.defaultValue !== undefined) {
      defaults[field.key] = field.defaultValue;
    }
  }
  return defaults;
}
